import { Box, Grid, Paper, Skeleton, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";

import { getCustomers } from "../../services/crm.service";
import { getAdminOrders } from "../../services/order.service";
import { getSupportTasks } from "../../services/task.service";
import { OrderStatus } from "../../types/order";

const ORDER_STATUSES: OrderStatus[] = [
  "RECEIVED",
  "CREATED",
  "REQUEST_MORE_INFO",
  "UPDATED",
  "CANCELLED",
];

type ReportState = {
  totalOrders: number;
  totalCustomers: number;
  orderByStatus: Record<OrderStatus, number>;
  supportPending: number;
  supportInProgress: number;
  supportCompleted: number;
};

const initialState: ReportState = {
  totalOrders: 0,
  totalCustomers: 0,
  orderByStatus: {
    RECEIVED: 0,
    CREATED: 0,
    REQUEST_MORE_INFO: 0,
    UPDATED: 0,
    CANCELLED: 0,
  },
  supportPending: 0,
  supportInProgress: 0,
  supportCompleted: 0,
};

export default function CrmReports() {
  const [data, setData] = useState<ReportState>(initialState);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [
          totalOrdersRes,
          totalCustomersRes,
          pending,
          inProgress,
          completed,
        ] = await Promise.all([
          getAdminOrders({ page: 1, page_size: 1 }),
          getCustomers({ page: 0, page_size: 1 }),
          getSupportTasks("pending"),
          getSupportTasks("in-progress"),
          getSupportTasks("complete"),
        ]);

        const byStatusResult = await Promise.all(
          ORDER_STATUSES.map((status) =>
            getAdminOrders({ page: 1, page_size: 1, statuses: [status] }),
          ),
        );

        const orderByStatus = ORDER_STATUSES.reduce(
          (acc, status, index) => {
            acc[status] = byStatusResult[index].total_items || 0;
            return acc;
          },
          { ...initialState.orderByStatus },
        );

        setData({
          totalOrders: totalOrdersRes.total_items || 0,
          totalCustomers: totalCustomersRes.total_records || 0,
          orderByStatus,
          supportPending: pending.length,
          supportInProgress: inProgress.length,
          supportCompleted: completed.length,
        });
      } catch (error) {
        console.error("Error loading reports:", error);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const renderMetric = (title: string, value: number) => (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {title}
      </Typography>
      <Typography variant="h5" sx={{ mt: 0.5 }}>
        {loading ? <Skeleton width={72} /> : value.toLocaleString()}
      </Typography>
    </Paper>
  );

  return (
    <Box>
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Stack spacing={0.5}>
          <Typography variant="h5">Reports</Typography>
          <Typography variant="body2" color="text.secondary">
            Live CRM summary from order, customer, and support services.
          </Typography>
        </Stack>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            {renderMetric("Total Orders", data.totalOrders)}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderMetric("Total Customers", data.totalCustomers)}
          </Grid>
        </Grid>

        <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
          Orders by Status
        </Typography>
        <Grid container spacing={2}>
          {ORDER_STATUSES.map((status) => (
            <Grid item xs={12} sm={6} md={4} key={status}>
              {renderMetric(status, data.orderByStatus[status])}
            </Grid>
          ))}
        </Grid>

        <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
          Customer Service Snapshot
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            {renderMetric("Pending", data.supportPending)}
          </Grid>
          <Grid item xs={12} sm={4}>
            {renderMetric("In Progress", data.supportInProgress)}
          </Grid>
          <Grid item xs={12} sm={4}>
            {renderMetric("Completed", data.supportCompleted)}
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
